import * as React from 'react';

import styled from '@emotion/styled';
import { Box } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { DropdownContainer, DropdownContent } from '@lightspeed/flame/Dropdown';
import { useEventListener, useOnClickOutside, usePopper, useToggle } from '@lightspeed/flame/hooks';
import { IconMathAdd } from '@lightspeed/flame/Icon/MathAdd';
import { IconSmallChevronDown } from '@lightspeed/flame/Icon/SmallChevronDown';
import { IconSmallChevronUp } from '@lightspeed/flame/Icon/SmallChevronUp';
import { IdentityObserver } from '@shopkeep/lsid-identity-observer';

import {
  getIdentityType,
  isOneLoginIdentity,
  isSessionIdentity,
  isUserIdentity,
  LsidIdentityType,
} from 'root/apps/pages/utils/lsid';
import { isScreenLocked } from 'root/apps/pages/utils/pin';
import Monitoring from 'shared_modules/Monitoring';

const SUPPORTED_PRODUCTS = [
  {
    key: 'retail',
    name: 'Retail (R-Series)',
    active: true,
  },
  {
    key: 'retail-sbx',
    name: 'Retail (R-Series)',
    active: true,
  },
  {
    key: 'golf',
    name: 'Golf',
  },
];

const SwitcherContainer = styled('div')`
  -webkit-font-smoothing: antialiased;
  line-height: 1.125rem;
  position: relative;

  .dropdown-container {
    width: calc(240px - 1.5rem);
    background-color: #2f3137;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: 0.1875rem;
    border-bottom-right-radius: 0.1875rem;
    border: 0;
    padding: 0;
    box-shadow: none;
    transition: none;
  }
`;

const ProductSwitcher = styled('button')`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 2.5rem;
  margin: 0;
  cursor: pointer;
  color: #ffffff;
  background-color: ${(props) => (props.isActive ? '#2e61de' : '#2f3137')};
  user-select: none;
  border: none;
  border-top-left-radius: 0.1875rem;
  border-top-right-radius: 0.1875rem;
  border-bottom-left-radius: ${(props) => (props.isActive ? 0 : '0.1875rem')};
  border-bottom-right-radius: ${(props) => (props.isActive ? 0 : '0.1875rem')};
  padding: 0 0.75rem;
  line-height: 1.125rem;
  font-weight: 700 !important;

  pointer-events: ${(props) => (props.isDisabled ? 'none' : 'auto')};

  &:hover {
    background-color: ${(props) => (props.isActive ? '#2e61de' : '#464a51')};
  }
`;

const DropdownList = styled('ul')`
  margin: 0;
  padding: 0;
`;

const DropdownItem = styled('li')`
  list-style-type: none;
  margin: 0;
  padding: 0;

  .dropdown-item-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 2.5rem;
    padding: 0 0.75rem;
    text-decoration: none;
    color: #fff;
    font-weight: 700 !important;
    font-size: 16px;

    &:hover {
      background-color: #464a51;
    }
  }

  .dashboard-link {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    font-size: 0.875rem;
    color: #fff;
    padding: 0.75rem;
    border-bottom-left-radius: 0.1875rem;
    border-bottom-right-radius: 0.1875rem;
    font-weight: normal !important;
  }

  .dashboard-link-info {
    color: #8b939b;
    font-size: 0.75rem;
  }
`;

const DropdownMessage = styled('div')`
  font-size: 0.875rem;
  padding: 0.75rem;

  p {
    color: #a3abb3;
    margin: 0;
    padding: 0;
  }
`;

const initialUserIdentity = {
  isLSIDUser: false,
  products: [],
  grants: [],
  jti: undefined,
  initialJti: undefined,
  identityType: undefined,
};

const formatUserIdentity = (domain, identityData) => {
  const grants = Object.keys(identityData?.grants?.user || {});
  const jti = identityData?.jti;
  const initialJti = identityData?.initialJti;
  const identityType = getIdentityType(identityData);

  if (identityType === LsidIdentityType.Unknown || !jti) {
    return initialUserIdentity;
  }

  const products = [];
  SUPPORTED_PRODUCTS.filter((product) => {
    return grants.includes(product.key);
  }).forEach((product) => {
    products.push({
      ...product,
      url: `${domain}/auth/lightspeed/${product.key}`,
    });
  });

  return { isLSIDUser: true, products, grants, initialJti, identityType, jti };
};

export const LsidProductSwitcher = ({
  domain,
  appDomain,
  showEcom,
  ecomLink,
  showEcomLite,
  cSeriesHideDropdown,
  ecomLiteLink,
  navigate,
  appName,
}) => {
  // Setup Custom Dropdown
  const targetRef = React.createRef();
  const popperRef = React.createRef();
  usePopper(targetRef, popperRef, {
    placement: 'bottom-end',
  });
  const { toggle, isActive } = useToggle();
  useOnClickOutside(popperRef, () => isActive && toggle());
  useEventListener((event) => {
    if (event.key === 'Escape' && isActive) {
      toggle();
    }
  }, 'keyup');

  // Load available products and features
  const [userIdentity, setUserIdentity] = React.useState(initialUserIdentity);
  const [retailUserID, setRetailUserID] = React.useState();
  const { isLSIDUser, products, grants, initialJti, jti, identityType } = userIdentity;
  const isReadOnly = window.merchantos?.session?.isReadOnly() || false;

  const isDropdownDisabled = (!isLSIDUser && !showEcom && !showEcomLite) || isReadOnly;

  // Will determine if we show the LSID Dashboard link
  const isShowLsidInvite = products.length === 1 && products[0].key === appName && !showEcom;

  // Only pull the first eCom grant, as this will cover most of our use cases.
  const ecomGrant = grants.find((grant) => grant.startsWith('ecom'));

  // For the identity change subcription
  const [identityObserver, setIdentityObserver] = React.useState();

  // To enable the observer
  const isIdentityObserverEnabled =
    identityType === LsidIdentityType.User || identityType === LsidIdentityType.Session;

  // Set eCom link
  const ecomSwitcherLink = ecomGrant ? `${domain}/auth/lightspeed/${ecomGrant}` : ecomLink;

  // Set "Link accounts" link with language query param
  const language = window.Localize ? window.Localize.getLanguage() : 'en';

  const linkAccountUrl = new URL(`/link-account?lang=${language}`, appDomain).toString();
  const lsidLinkAccountUrl = new URL(
    `/auth/lightspeed/lsid?redirect_after_login=${encodeURIComponent(linkAccountUrl)}`,
    domain
  ).toString();

  // Gets the Retail user ID to compare against the LSID grants
  React.useEffect(() => {
    window.merchantos.session.init().done((response) => {
      const session = window.merchantos.session.sessData;
      setRetailUserID(session.systemUserID);
    });
  }, []);

  // Initialize the identity observer
  React.useEffect(() => {
    const observer = new IdentityObserver({
      lsApiBaseUrl: domain,
    });

    setIdentityObserver(observer);

    return () => {
      observer.cleanupWatchers();
    };
  }, [domain]);

  // Initialize call to identity service
  React.useEffect(() => {
    const fetchData = async () => {
      if (identityObserver) {
        let identityData;

        try {
          const identityResponse = await identityObserver.fetchIdentity();

          if (identityObserver.isAutoLogoutEnabled() && identityResponse.status === 401) {
            window.merchantos.utils.openUrl('/auth/logout');
          } else {
            identityData = identityResponse.data;
          }
        } catch (err) {
          Monitoring.addError(err, 'LsidProductSwitcher');
        }

        setUserIdentity(formatUserIdentity(domain, identityData));
      }
    };

    fetchData();
  }, [domain, identityObserver]);

  const handleIdentityObserverEvent = React.useCallback(
    (event) => {
      switch (event.type) {
        case 'UPDATED':
          if (isOneLoginIdentity(event.identity)) {
            Monitoring.addEvent('lsid.is_lightspeed_one_login', 'LsidProductSwitcher');
            return;
          }

          // In case the token has not changed, we don't want to do anything.
          if (event.identity.jti === jti) {
            return;
          }

          const isUserToSessionExchange =
            identityType === LsidIdentityType.User && isSessionIdentity(event.identity);
          const isSessionToUserExchange =
            identityType === LsidIdentityType.Session && isUserIdentity(event.identity);
          const isCurrentSessionUpdated =
            getIdentityType(event.identity) === identityType &&
            event.identity.grants?.user?.[appName]?.includes(retailUserID);

          // We perform the token exchange in a background iframe while remaining on the current page.
          // During this scenario (either going from session -> user token, or user -> session token),
          // we don't want to perform any page reloads or redirects, and instead we just update the
          // identity value in the background as well.
          if ((isUserToSessionExchange && isScreenLocked()) || isSessionToUserExchange) {
            setUserIdentity(formatUserIdentity(domain, event.identity));
          } else if (identityObserver.isAutoLogoutEnabled() && identityObserver.isSession()) {
            window.location.reload();
          } else if (isCurrentSessionUpdated) {
            setUserIdentity(formatUserIdentity(domain, event.identity));
          } else if (identityObserver.isAutoLogoutEnabled()) {
            window.merchantos.utils.openUrl(`${domain}/auth/lightspeed/retail`);
          }
          break;
        case 'UNAUTHORIZED':
          if (identityObserver.isAutoLogoutEnabled()) {
            window.merchantos.utils.openUrl('/auth/logout');
          }
          break;
        case 'ERROR':
          Monitoring.addError(event.error, 'LsidProductSwitcher');
          break;
        default:
          Monitoring.addEvent('lsid.observer_event_undefined', 'LsidProductSwitcher');
          break;
      }
    },
    [appName, domain, identityObserver, identityType, jti, retailUserID]
  );

  // Subscribe to identity observer events
  React.useEffect(() => {
    let unsubscribeIdentityObserver;

    if (identityObserver && isIdentityObserverEnabled && retailUserID) {
      unsubscribeIdentityObserver = identityObserver.subscribe(handleIdentityObserverEvent);
    }

    return function cleanup() {
      if (unsubscribeIdentityObserver) {
        unsubscribeIdentityObserver();
      }
    };
  }, [handleIdentityObserverEvent, identityObserver, isIdentityObserverEnabled, retailUserID]);

  // eCom link click logic
  const handleEcomClick = (e) => {
    toggle();

    if (ecomGrant) {
      window.merchantos.utils.openUrl(ecomSwitcherLink);
      e.preventDefault();
    } else {
      navigate({
        e,
        action: () =>
          (window.ecomLink || ecomLink) === '/ecom/provisioning'
            ? window.PageRouter.goTo(ecomSwitcherLink)
            : window.merchantos.utils.openUrl(ecomSwitcherLink),
      });
    }
  };

  return (
    <SwitcherContainer data-test="lsid-product-switcher">
      <ProductSwitcher
        type="button"
        ref={targetRef}
        onClick={isDropdownDisabled ? () => {} : toggle}
        isActive={isActive}
        isDisabled={isDropdownDisabled}
      >
        <Box>
          <span className="notranslate">Retail (R-Series)</span>
        </Box>
        {!isDropdownDisabled && (
          <Box marginTop="-2px">{isActive ? <IconSmallChevronUp /> : <IconSmallChevronDown />}</Box>
        )}
      </ProductSwitcher>
      {isActive && (
        <DropdownContainer
          ref={popperRef}
          isActive={isActive}
          zIndex={100}
          className="dropdown-container"
        >
          {showEcomLite && (
            <React.Fragment>
              <DropdownContent m={0}>
                <DropdownList>
                  <DropdownItem className="ecomLite">
                    <a
                      href={ecomLiteLink}
                      className="dropdown-item-link _flex-layout notranslate"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      eCom (E-Series)
                    </a>
                  </DropdownItem>
                </DropdownList>
              </DropdownContent>
              <Divider color="#464A51" />
            </React.Fragment>
          )}

          {(showEcom || ecomGrant) && !cSeriesHideDropdown && (
            <DropdownContent m={0}>
              <DropdownList>
                <DropdownItem>
                  <a
                    href={ecomSwitcherLink}
                    className="dropdown-item-link notranslate"
                    onClick={handleEcomClick}
                  >
                    eCom (C-Series)
                  </a>
                </DropdownItem>
              </DropdownList>
            </DropdownContent>
          )}

          {!!products.length && (
            <React.Fragment>
              <DropdownContent m={0}>
                <DropdownList>
                  {products.map(
                    (product) =>
                      !product.active && (
                        <DropdownItem key={product.key}>
                          <a
                            href={product.url}
                            className="dropdown-item-link notranslate"
                            data-test={`lsid-product-link-${product.key}`}
                            onClick={(e) =>
                              navigate({
                                e,
                                action: () => {
                                  window.merchantos.utils.openUrl(product.url);
                                },
                              })
                            }
                          >
                            {product.name}
                          </a>
                        </DropdownItem>
                      )
                  )}
                </DropdownList>
              </DropdownContent>
            </React.Fragment>
          )}

          {isShowLsidInvite && (
            <React.Fragment>
              <Divider color="#464A51" />
              <DropdownContent m={0}>
                <DropdownMessage>
                  <p data-test="lsid-product-switcher-message">
                    More than one Lightspeed account? Link your accounts to enable one-step
                    switching.
                  </p>
                </DropdownMessage>
              </DropdownContent>
            </React.Fragment>
          )}

          {isLSIDUser && (
            <React.Fragment>
              <Divider color="#464A51" />
              <DropdownContent m={0}>
                <DropdownList>
                  <DropdownItem className="dropdown-item">
                    {/* eslint-disable-next-line react/jsx-no-target-blank */}
                    <a
                      href={lsidLinkAccountUrl}
                      target="_blank"
                      rel="noopener"
                      className="dropdown-item-link dashboard-link"
                      data-test="lsid-product-switcher-link-account"
                    >
                      <span>
                        Link accounts <br />
                        <span className="dashboard-link-info">Link another Lightspeed account</span>
                      </span>
                      <IconMathAdd />
                    </a>
                  </DropdownItem>
                </DropdownList>
              </DropdownContent>
            </React.Fragment>
          )}
        </DropdownContainer>
      )}
    </SwitcherContainer>
  );
};
